import { FocusEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import ClickAwayListener from "@mui/material/ClickAwayListener";

import { formatStringArray } from "shared/locale/format";

import Tooltip from "features/components/Tooltip";

interface Props {
  items: string[];
  hiddenCount: number;
  separator: string;
}

// A click opens the full list too, since touch screens get no hover tooltip. The click and the Space key stop here:
// the table row opens the record drawer on click, and the data grid moves focus on Space.
const MoreItemsButton = ({ items, hiddenCount, separator }: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const moreLabel = (count: number) => t("common:chip_list.more", { count });

  return (
    <ClickAwayListener
      mouseEvent={open ? "onClick" : false}
      touchEvent={open ? "onTouchEnd" : false}
      onClickAway={() => setOpen(false)}
    >
      <span className="shrink-0">
        <Tooltip
          title={formatStringArray(
            items,
            { separator, maxItems: null },
            moreLabel
          )}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={(event) => {
            // A press on the tooltip text moves focus to nothing. That blur must not close it; the click-away does.
            if (event.type === "blur" && !(event as FocusEvent).relatedTarget) {
              return;
            }

            setOpen(false);
          }}
          underline
          describeChild
        >
          <button
            type="button"
            // The grid cell's line height is the row height, which would clip the focus ring.
            className="leading-none"
            onClick={(event) => {
              event.stopPropagation();
              setOpen(true);
            }}
            onKeyDown={(event) => {
              if (event.key === " ") event.stopPropagation();
            }}
          >
            {moreLabel(hiddenCount)}
          </button>
        </Tooltip>
      </span>
    </ClickAwayListener>
  );
};

export default MoreItemsButton;
